import React from 'react';
import {
  TextField,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
} from '@elementary/common-ui/components';
import { FormControlLabel, Checkbox, Collapse } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { MAX_PORT, MIN_PORT } from './constants';
import { getSshPortWhenEnabling, isValidPort } from './utils';

type Props = {
  disabled?: boolean;
};

const SshTunnelFormSection = ({ disabled }: Props) => {
  const methods = useFormContext();
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const sshEnabled = watch('sshEnabled');

  return (
    <FlexColumn gap="inherit">
      <FormControlLabel
        control={
          <Checkbox
            checked={!!sshEnabled}
            onChange={(e) => {
              setValue('sshEnabled', e.target.checked);
              const sshPort = getSshPortWhenEnabling(
                e.target.checked,
                methods.getValues('sshPort'),
              );
              if (sshPort !== undefined) {
                setValue('sshPort', sshPort);
              }
            }}
            disabled={disabled}
          />
        }
        label="Connect via SSH tunnel"
      />
      <Collapse in={!!sshEnabled}>
        <FlexColumn sx={{ gap: '24px' }}>
          <FormField
            title="SSH Host"
            errorMessage={errors.sshHost?.message as string}
          >
            <TextField
              {...register('sshHost', {
                required: sshEnabled ? requiredLabel : false,
              })}
              variant="outlined"
              placeholder="e.g. bastion.example.com"
              disabled={disabled}
            />
          </FormField>
          <FormField
            title="SSH Port"
            errorMessage={errors.sshPort?.message as string}
          >
            <TextField
              {...register('sshPort', {
                required: sshEnabled ? requiredLabel : false,
                valueAsNumber: true,
                validate: (value) => {
                  if (!sshEnabled) {
                    return true;
                  }
                  return (
                    isValidPort(value) ||
                    `SSH port must be an integer between ${MIN_PORT} and ${MAX_PORT}`
                  );
                },
              })}
              variant="outlined"
              placeholder="22"
              type="number"
              disabled={disabled}
            />
          </FormField>
          <FormField
            title="SSH Username"
            errorMessage={errors.sshUsername?.message as string}
          >
            <TextField
              {...register('sshUsername', {
                required: sshEnabled ? requiredLabel : false,
              })}
              variant="outlined"
              placeholder="e.g. ec2-user"
              disabled={disabled}
            />
          </FormField>
        </FlexColumn>
      </Collapse>
    </FlexColumn>
  );
};

export default SshTunnelFormSection;
